/**
 * SessionWaitingRoomTab
 *
 * Facilitator-side waiting room panel rendered inside SessionDetail.
 * Shows the live roster of participants who have entered the waiting room
 * for this session, sourced from the `session_presence` table via Supabase
 * Realtime.
 *
 * The facilitator can broadcast a "Start Session" signal by inserting a
 * `waiting_room_started` event into `session_events`. All participants
 * subscribed to that channel advance out of the waiting room automatically.
 *
 * Build Contract alignment (§6.4 Live Session Collaboration):
 *   • `session_presence` is the durable record; client state derives from it.
 *   • `session_events` is the append-only action log; `waiting_room_started`
 *     is the facilitator signal that unblocks participants.
 *   • No optimistic governance transitions (§2.3a).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Users, Radio, RefreshCw, LogIn } from 'lucide-react';
import { getSafeSupabaseClient, getCurrentWorkspaceId, generateUUID } from '../../../lib/supabaseSync';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { PrioritizationSession } from '../../../types/prioritization/session';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface PresenceRow {
  id: string;
  session_id: string;
  user_id: string;
  user_name: string;
  joined_at: string;
  last_ping_at: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function initials(name: string): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

/** Row is considered stale when last_ping_at is >60 s ago. */
function isStale(lastPingAt: string): boolean {
  try {
    return Date.now() - new Date(lastPingAt).getTime() > 60_000;
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  session: PrioritizationSession;
}

export function SessionWaitingRoomTab({ session }: Props) {
  const userId   = useUserStore(s => s.userId);
  const userName = useUserStore(s => s.userName);

  const [rows, setRows]             = useState<PresenceRow[]>([]);
  const [loading, setLoading]       = useState(true);
  const [signaling, setSignaling]   = useState(false);
  const [signaled, setSignaled]     = useState(false);
  const [signalError, setSignalError] = useState<string | null>(null);
  const channelRef = useRef<ReturnType<NonNullable<ReturnType<typeof getSafeSupabaseClient>>['channel']> | null>(null);

  // ── Initial fetch ──────────────────────────────────────────────────────────
  const fetchPresence = useCallback(async () => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('session_presence')
      .select('*')
      .eq('session_id', session.id)
      .order('joined_at', { ascending: true });
    if (!error && data) setRows(data as PresenceRow[]);
    setLoading(false);
  }, [session.id]);

  useEffect(() => {
    fetchPresence();
  }, [fetchPresence]);

  // ── Realtime subscription ──────────────────────────────────────────────────
  useEffect(() => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const channel = supabase
      .channel(`session_presence:${session.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'session_presence',
          filter: `session_id=eq.${session.id}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setRows(prev => {
              const exists = prev.some(r => r.id === (payload.new as PresenceRow).id);
              return exists ? prev : [...prev, payload.new as PresenceRow];
            });
          } else if (payload.eventType === 'UPDATE') {
            setRows(prev =>
              prev.map(r => r.id === (payload.new as PresenceRow).id ? payload.new as PresenceRow : r)
            );
          } else if (payload.eventType === 'DELETE') {
            setRows(prev => prev.filter(r => r.id !== (payload.old as { id: string }).id));
          }
        }
      )
      .subscribe();

    channelRef.current = channel;
    return () => {
      supabase.removeChannel(channel);
    };
  }, [session.id]);

  // ── Signal start ───────────────────────────────────────────────────────────
  const handleSignalStart = useCallback(async () => {
    if (signaling || signaled) return;
    const supabase  = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId() ?? session.workspaceId ?? '';
    if (!supabase || !workspaceId) return;

    setSignaling(true);
    setSignalError(null);

    const { error } = await supabase
      .from('session_events')
      .insert({
        id:           generateUUID(),
        session_id:   session.id,
        workspace_id: workspaceId,
        actor_id:     userId,
        actor_name:   userName || 'Facilitator',
        event_type:   'waiting_room_started',
        payload:      { started_by: userId, session_name: session.name },
      });

    if (error) {
      setSignalError(error.message);
    } else {
      setSignaled(true);
    }
    setSignaling(false);
  }, [signaling, signaled, session.id, session.name, session.workspaceId, userId, userName]);

  // ── Derived state ──────────────────────────────────────────────────────────
  const active = rows.filter(r => !isStale(r.last_ping_at));
  const stale  = rows.filter(r =>  isStale(r.last_ping_at));

  return (
    <div className="space-y-6">

      {/* Header ──────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">
            Waiting Room
          </h3>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] max-w-prose">
            Participants who navigate to this session before ranking begins
            appear here in real time. Use <strong>Signal Start</strong> to
            broadcast a start event — participants in the waiting room advance
            to the session automatically.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchPresence}
          className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--bg-raised)] rounded transition-colors flex-shrink-0"
          title="Refresh presence list"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Live count pill ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          <span className="w-2 h-2 rounded-full bg-[var(--coral,#C0533A)] animate-pulse flex-shrink-0" />
          <Users size={13} className="opacity-60" />
          {loading ? '…' : active.length}
          {' '}
          {active.length === 1 ? 'participant' : 'participants'} present
        </div>
      </div>

      {/* Roster ─────────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] animate-pulse py-4">
          Loading presence…
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-10 border border-dashed border-[var(--rule)] dark:border-[var(--rule)]">
          <LogIn size={24} className="text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] text-center max-w-xs">
            No participants in the waiting room yet. Share the session link
            and participants will appear here when they arrive.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {active.length > 0 && (
            <div>
              <div className="text-xs font-medium tracking-widest uppercase text-[var(--text-ter)] dark:text-[var(--text-dim)] mb-2">
                Present now
              </div>
              <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
                {active.map(row => (
                  <PresenceListRow key={row.id} row={row} currentUserId={userId} />
                ))}
              </div>
            </div>
          )}
          {stale.length > 0 && (
            <div>
              <div className="text-xs font-medium tracking-widest uppercase text-[var(--text-ter)] dark:text-[var(--text-dim)] mb-2">
                Disconnected (last seen &gt;1 min ago)
              </div>
              <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden opacity-50">
                {stale.map(row => (
                  <PresenceListRow key={row.id} row={row} currentUserId={userId} stale />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Signal start ────────────────────────────────────────────────────── */}
      <div className="border-t border-[var(--rule)] dark:border-[var(--rule)] pt-5">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <div className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-sec)] mb-1">
              Signal Session Start
            </div>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              Sends a <code className="font-mono bg-[var(--hover-tint)] dark:bg-[var(--bg-raised)] px-1 rounded text-xs">waiting_room_started</code> event.
              Participants in the waiting room are forwarded to the session brief automatically.
              You can signal start even when no one is in the waiting room.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSignalStart}
            disabled={signaling || signaled}
            className={[
              'flex items-center gap-2 px-4 py-2 text-sm font-medium  transition-all whitespace-nowrap flex-shrink-0',
              signaled
                ? 'bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)] cursor-default'
                : 'bg-primary-600 hover:bg-primary-700 text-white border border-transparent disabled:opacity-50 disabled:cursor-not-allowed',
            ].join(' ')}
          >
            <Radio size={14} />
            {signaled ? 'Started' : signaling ? 'Signaling…' : 'Signal Start'}
          </button>
        </div>
        {signalError && (
          <p className="mt-2 text-xs text-[var(--coral)] dark:text-[var(--coral)]">{signalError}</p>
        )}
        {signaled && (
          <p className="mt-2 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
            Start signal sent — participants will advance to the session brief.
          </p>
        )}
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-component: single presence row
// ─────────────────────────────────────────────────────────────────────────────

function PresenceListRow({
  row,
  currentUserId,
  stale = false,
}: {
  row: PresenceRow;
  currentUserId: string;
  stale?: boolean;
}) {
  const isMe = row.user_id === currentUserId;

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)] hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)] transition-colors">
      <div
        className={[
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
          'text-xs font-bold font-mono',
          isMe
            ? 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 text-[var(--mustard)] dark:text-[var(--mustard)] border border-[var(--mustard)] dark:border-[var(--mustard)]/40'
            : 'bg-[var(--hover-tint)] dark:bg-[var(--bg-raised)] text-[var(--text-ter)] dark:text-[var(--text-ter)] border border-[var(--rule)] dark:border-[var(--rule)]',
        ].join(' ')}
      >
        {initials(row.user_name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-sec)] truncate">
          {row.user_name || row.user_id.slice(0, 8)}
          {isMe && (
            <span className="ml-1.5 text-xs font-mono text-[var(--mustard)] dark:text-[var(--mustard)] opacity-75">
              (you)
            </span>
          )}
        </div>
        <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] font-mono">
          {stale ? 'Last seen ' : 'Joined '}
          {formatTime(stale ? row.last_ping_at : row.joined_at)}
        </div>
      </div>
      <div className={[
        'w-2 h-2 rounded-full flex-shrink-0',
        stale
          ? 'bg-[var(--rule-bright)] dark:bg-[var(--rule-bright)]'
          : 'bg-[var(--green)] animate-pulse',
      ].join(' ')} />
    </div>
  );
}
